import { getSupabase } from './supabase';

// Koala Pro — the paid plan that unlocks the AI features (AI Advisor +
// Build-from-description). Everything else in the app stays free forever.
//
// Billing runs through Lemon Squeezy (a Merchant of Record — it collects and
// remits global VAT/sales tax, and supports Israel, which Stripe does not).
//
// Rollout switch: the paywall only *enforces* when REACT_APP_BILLING_ENABLED is
// "1" (client) and LEMONSQUEEZY_API_KEY is set (server). Until then the app
// behaves exactly as before — AI just requires sign-in — so nothing breaks
// before billing is fully wired up.

export const AI_PAYWALL_ENABLED = process.env.REACT_APP_BILLING_ENABLED === '1';

// Free signed-in users get a taste of the AI before the upgrade prompt.
export const FREE_AI_CREDITS = 3;

// Prices are display-only here (the real charge amount lives in the Lemon
// Squeezy variant referenced by LEMONSQUEEZY_VARIANT_ID on the server). Keep
// in sync with your Lemon Squeezy dashboard. Koala Pro is a single monthly plan.
export const PRICING = {
  currency: 'USD',
  symbol: '$',
  monthly: { amount: 9, per: 'month', interval: 'month' },
};

export const PRO_FEATURES = [
  'Unlimited AI Advisor: ask anything about your model and apply changes instantly',
  'Unlimited Build-from-description: a full 3-statement model from a sentence',
  'Priority AI model access',
  'Everything in Free: unlimited models, scenarios, benchmarks, Excel and sharing',
];

// ── Reading the current plan ────────────────────────────────────────────────

const FREE = { plan: 'free', status: null, aiCreditsUsed: 0, currentPeriodEnd: null, cancelAtPeriodEnd: false };

// Reads the signed-in user's subscription row. Returns a normalized object, or
// null when there's no Supabase (local-only mode) or no signed-in user.
export async function fetchSubscription() {
  const supabase = await getSupabase();
  if (!supabase) return null;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data, error } = await supabase
    .from('subscriptions')
    .select('plan,status,interval,current_period_end,cancel_at_period_end,ai_credits_used')
    .eq('user_id', user.id)
    .maybeSingle();
  if (error || !data) return { ...FREE };
  return {
    plan: data.plan || 'free',
    status: data.status || null,
    interval: data.interval || null,
    aiCreditsUsed: data.ai_credits_used || 0,
    currentPeriodEnd: data.current_period_end || null,
    cancelAtPeriodEnd: !!data.cancel_at_period_end,
  };
}

export function isPro(sub) {
  return !!sub && sub.plan === 'pro' && (sub.status === 'active' || sub.status === 'trialing');
}

export function remainingFreeCredits(sub) {
  if (!sub) return 0;
  return Math.max(0, FREE_AI_CREDITS - (sub.aiCreditsUsed || 0));
}

// True when the user is allowed to fire an AI action right now. When the paywall
// is disabled everyone with an account passes; otherwise Pro or leftover credits.
export function canUseAI(sub) {
  if (!AI_PAYWALL_ENABLED) return true;
  return isPro(sub) || remainingFreeCredits(sub) > 0;
}

// ── Checkout + billing portal (redirect flows) ──────────────────────────────

async function authedFetch(path, body) {
  const supabase = await getSupabase();
  if (!supabase) throw new Error('Sign in required.');
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Sign in required.');
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body || {}),
  });
  let json = {};
  try { json = await res.json(); } catch {}
  if (!res.ok) throw new Error(json.error || 'Something went wrong. Please try again.');
  return json;
}

// ── Checkout prefetch ───────────────────────────────────────────────────────
// Creating a checkout costs two sequential round trips on the server (verify
// the Supabase session, then create the Lemon Squeezy checkout), so doing it
// on click leaves the user staring at a dead button. Instead the pricing modal
// starts the request when it opens and we reuse the in-flight promise, so by
// the time anyone actually clicks, the URL is usually already here.
//
// The cache has to be invalidatable, not just memoized: the server bakes the
// buyer's id and email into the checkout, and the webhook credits whichever
// user_id it finds there. Reusing one account's checkout after a different
// user signs in would pay their money into the first account — hence the
// returnUrl key, the TTL, and clearCheckoutCache() on auth changes.
let checkoutCache = null;   // { key, promise, createdAt }
const CHECKOUT_TTL_MS = 10 * 60 * 1000;

export function clearCheckoutCache() { checkoutCache = null; }

export function prefetchCheckout() {
  const key = window.location.origin + window.location.pathname;   // == returnUrl
  const fresh = checkoutCache
    && checkoutCache.key === key
    && Date.now() - checkoutCache.createdAt < CHECKOUT_TTL_MS;
  if (!fresh) {
    // Drop the cache on failure so a retry issues a fresh request rather than
    // replaying the same rejection forever.
    const promise = authedFetch('/api/create-checkout-session', { returnUrl: key })
      .catch(err => { checkoutCache = null; throw err; });
    checkoutCache = { key, promise, createdAt: Date.now() };
  }
  return checkoutCache.promise;
}

// Kicks off Lemon Squeezy Checkout and redirects the browser.
export async function startCheckout() {
  const { url } = await prefetchCheckout();
  if (url) window.location.assign(url);
}

// Opens the Stripe billing portal so a Pro user can manage or cancel.
export async function openBillingPortal() {
  const { url } = await authedFetch('/api/create-portal-session', {
    returnUrl: window.location.origin + window.location.pathname,
  });
  if (url) window.location.assign(url);
}
